// Thin client for the booth-tracker backend. Every call carries the user's
// delegated Entra bearer token (same token the MCP accepts). All calls are made
// only by the sync engine, only when online — capture never depends on these.

import { API_BASE } from '../config';
import { getMcpToken, getMcpTokenInteractive } from '../auth/auth';
import type { BoothRecord, HitListTarget } from '../db/schema';
import type { SnapshotPayload } from '../snapshot/types';
import type { Product360 } from '../product/types';
import type { Vendor360 } from '../vendors/types';

export class NotAuthedError extends Error {
  constructor() {
    super('no delegated token available — sign in while online');
  }
}

/** True for the one failure a screen can fix by asking the user to sign in. */
export const isNotAuthed = (err: unknown): boolean => err instanceof NotAuthedError;

/**
 * Bearer header for a backend call.
 *
 * `interactive` is opt-in and means "a person is waiting on this": the token helper
 * may open a sign-in popup and retry. It is passed ONLY by the three user-initiated
 * reports (vendor 360 products, product 360, snapshot build). Everything else —
 * every path the sync engine can reach — keeps the silent default, because a
 * background sync must never pop a window in a buyer's face mid-aisle.
 */
async function authHeader(interactive = false): Promise<Record<string, string>> {
  const token = interactive ? await getMcpTokenInteractive() : await getMcpToken();
  if (!token) throw new NotAuthedError();
  // NOT `Authorization`: Azure Static Web Apps overwrites that header with an
  // internal platform token before forwarding to the managed Functions backend,
  // so the delegated Entra token never arrives. A custom header is forwarded
  // untouched; the backend reads `X-Booth-Token` (see api/src/shared/auth.ts).
  return { 'X-Booth-Token': token };
}

/** Upsert a booth record. Backend merges by show + booth_number. */
export async function pushBooth(record: BoothRecord): Promise<void> {
  const res = await fetch(`${API_BASE}/booths`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeader()) },
    body: JSON.stringify(record),
  });
  if (!res.ok) throw new Error(`pushBooth failed: ${res.status} ${await res.text()}`);
}

/** Upload one compressed photo; returns its server URL (an /api path). */
export async function pushPhoto(boothId: string, localId: string, blob: Blob): Promise<string> {
  const res = await fetch(`${API_BASE}/photos/${encodeURIComponent(boothId)}/${encodeURIComponent(localId)}`, {
    method: 'POST',
    headers: { 'Content-Type': blob.type || 'image/jpeg', ...(await authHeader()) },
    body: blob,
  });
  if (!res.ok) throw new Error(`pushPhoto failed: ${res.status} ${await res.text()}`);
  const data = (await res.json()) as { blob_url: string };
  return data.blob_url;
}

/** List booths visible to this account (cross-device view). */
export async function fetchBooths(): Promise<BoothRecord[]> {
  const res = await fetch(`${API_BASE}/booths`, { headers: { ...(await authHeader()) } });
  if (!res.ok) throw new Error(`fetchBooths failed: ${res.status}`);
  const data = (await res.json()) as { booths: BoothRecord[] };
  return data.booths;
}

/** Upsert one hit-list target. Backend merges by show + exhibitor_key. */
export async function pushHitListTarget(target: HitListTarget): Promise<void> {
  const res = await fetch(`${API_BASE}/hitlist`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeader()) },
    body: JSON.stringify(target),
  });
  if (!res.ok) throw new Error(`pushHitListTarget failed: ${res.status} ${await res.text()}`);
}

/** The whole shared hit list, tombstones included (the client hides them). */
export async function fetchHitList(): Promise<HitListTarget[]> {
  const res = await fetch(`${API_BASE}/hitlist`, { headers: { ...(await authHeader()) } });
  if (!res.ok) throw new Error(`fetchHitList failed: ${res.status}`);
  const data = (await res.json()) as { targets: HitListTarget[] };
  return data.targets;
}

/**
 * Fetch a stored photo by its blob_url (an `/api/photos/...` path). The endpoint
 * is authenticated, so an <img src> can't load it directly — we fetch it with the
 * bearer token and hand back a Blob the caller turns into an object URL.
 */
export async function fetchPhoto(blobUrl: string): Promise<Blob> {
  const res = await fetch(blobUrl, { headers: { ...(await authHeader()) } });
  if (!res.ok) throw new Error(`fetchPhoto failed: ${res.status}`);
  return res.blob();
}

/**
 * Build + fetch the offline snapshot (the backend runs the delegated MCP pulls).
 * This is a heavier call than the others (bulk reads over the show), so callers
 * trigger it deliberately on wifi. On failure the backend self-reports the real
 * stage/status/detail — surface it so a snag names itself.
 */
export async function fetchSnapshot(interactive = false): Promise<SnapshotPayload> {
  const res = await fetch(`${API_BASE}/snapshot`, { headers: { ...(await authHeader(interactive)) } });
  if (!res.ok) {
    let detail = `${res.status}`;
    try {
      const body = (await res.json()) as { error?: string; stage?: string; detail?: string };
      detail = `${res.status} ${body.error ?? ''}${body.stage ? ` [${body.stage}]` : ''}${body.detail ? `: ${body.detail}` : ''}`;
    } catch {
      /* non-JSON error body */
    }
    throw new Error(`snapshot build failed: ${detail}`);
  }
  return (await res.json()) as SnapshotPayload;
}

/**
 * Fetch the live buyer's report for one product (backend calls get_product_360
 * over the delegated MCP session). ONLINE-only — a live MCP call, no offline
 * cache. On an MCP/auth FAILURE the backend self-reports stage/detail in the
 * body (temp, Slice 5) — surface it so a snag names itself. A product_not_found
 * / multiple_matches result is a valid 200 and comes back in the Product360.
 */
export async function fetchProduct360(id: string, interactive = false): Promise<Product360> {
  const res = await fetch(`${API_BASE}/product/${encodeURIComponent(id)}`, {
    headers: { ...(await authHeader(interactive)) },
  });
  if (!res.ok) {
    let detail = `${res.status}`;
    try {
      const body = (await res.json()) as { error?: string; stage?: string; detail?: string };
      detail = `${res.status} ${body.error ?? ''}${body.stage ? ` [${body.stage}]` : ''}${body.detail ? `: ${body.detail}` : ''}`;
    } catch {
      /* non-JSON error body */
    }
    throw new Error(`product report failed: ${detail}`);
  }
  return (await res.json()) as Product360;
}

/**
 * Fetch the live buy-side report for one vendor (backend calls get_vendor_360
 * over the delegated MCP session). ONLINE-only — powers the "products we buy
 * from them" drill-down. On an MCP/auth FAILURE the backend self-reports
 * stage/detail in the body (temp, this slice) — surface it so a snag names
 * itself. A vendor_not_found result is a valid 200 in the Vendor360.
 */
export async function fetchVendor360(id: string, interactive = false): Promise<Vendor360> {
  const res = await fetch(`${API_BASE}/vendor/${encodeURIComponent(id)}`, {
    headers: { ...(await authHeader(interactive)) },
  });
  if (!res.ok) {
    let detail = `${res.status}`;
    try {
      const body = (await res.json()) as { error?: string; stage?: string; detail?: string };
      detail = `${res.status} ${body.error ?? ''}${body.stage ? ` [${body.stage}]` : ''}${body.detail ? `: ${body.detail}` : ''}`;
    } catch {
      /* non-JSON error body */
    }
    throw new Error(`vendor report failed: ${detail}`);
  }
  return (await res.json()) as Vendor360;
}
